import { supabase } from '@/integrations/supabase/client';
import { emitMfaLifecycleEvent } from '@/lib/auditMfaEvent';

export interface TotpEnrollmentData {
  id: string;
  qr: string;
  secret: string;
}

type TotpFactorLike = {
  id: string;
  status?: string;
};

export async function enrollTotpFactor(): Promise<
  | { status: 'already-enabled' }
  | { status: 'enrolled'; enrollment: TotpEnrollmentData }
> {
  const { data: existingFactors } = await supabase.auth.mfa.listFactors();

  const verifiedFactor = existingFactors?.totp?.find((factor) => factor.status === 'verified');
  if (verifiedFactor) {
    return { status: 'already-enabled' };
  }

  const unverifiedFactors =
    existingFactors?.totp?.filter((factor) => (factor as TotpFactorLike).status === 'unverified') || [];

  for (const factor of unverifiedFactors) {
    try {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
      await emitMfaLifecycleEvent({
        eventType: 'mfa.factor_enroll_cancelled',
        factorId: factor.id,
        note: 'cleanup of stale unverified factor before fresh enrolment',
      });
    } catch (unenrollError) {
      console.warn('Failed to unenroll factor:', factor.id, unenrollError);
    }
  }

  const attemptEnroll = async (friendlyName: string) => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName,
      issuer: 'Supply Guard',
    });

    if (error) {
      throw error;
    }

    await emitMfaLifecycleEvent({
      eventType: 'mfa.factor_enroll_started',
      factorId: data.id,
    });

    return {
      id: data.id,
      qr: data.totp.qr_code,
      secret: data.totp.secret,
    } satisfies TotpEnrollmentData;
  };

  try {
    const enrollment = await attemptEnroll('Authenticator App');
    return { status: 'enrolled', enrollment };
  } catch (error: any) {
    if (error?.message?.includes('already exists') || error?.code === 'mfa_factor_name_conflict') {
      const enrollment = await attemptEnroll(`Authenticator App ${Date.now()}`);
      return { status: 'enrolled', enrollment };
    }

    throw error;
  }
}

/**
 * The one place an enrolling factor is verified. `mfa.factor_enrolled` means a
 * verified factor exists, so it is written here and only after the code is
 * accepted; a rejected code throws and writes nothing.
 */
export async function verifyTotpEnrollment(factorId: string, code: string): Promise<void> {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });

  if (error) {
    throw error;
  }

  await emitMfaLifecycleEvent({
    eventType: 'mfa.factor_enrolled',
    factorId,
  });
}
